import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Mesa, CreateMesaRequest, UpdateMesaRequest, Local } from '../types';
import { mesasApi, locaisApi, permissoesApi, reservasApi } from '../services/api';
import { Plus, Edit, Trash2, Users, Search, LayoutGrid, UserRound, Ruler, X, Table, Copy, Minus } from 'lucide-react';

interface MesaForm {
  localId: string;
  descricao: string;
  numero: string;
  capacidadePessoas: string;
  podeReservar: boolean;
  mesaDefault: boolean;
}

const emptyForm: MesaForm = { localId: '', descricao: '', numero: '', capacidadePessoas: '4', podeReservar: true, mesaDefault: false };

export function MesasPage() {
  const { user } = useAuth();
  const { success, error } = useToast();
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [localFiltro, setLocalFiltro] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Mesa | null>(null);
  const [form, setForm] = useState<MesaForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [mesaPermission, setMesaPermission] = useState({ access: false, create: false, edit: false, delete: false });
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [estadosHoje, setEstadosHoje] = useState<Map<number, number>>(new Map());

  const loadPermissions = useCallback(async () => {
    if (user?.roleId === undefined) return;
    try {
      const response = await permissoesApi.getAll(user.roleId);
      const permission = response.data?.find(item => item.recurso === 'mesas');
      setMesaPermission({
        access: !!permission?.podeAceder,
        create: !!permission?.podeCriar,
        edit: !!permission?.podeEditar,
        delete: !!permission?.podeEliminar,
      });
    } catch {
      setMesaPermission({ access: false, create: false, edit: false, delete: false });
    } finally {
      setPermissionLoading(false);
    }
  }, [user?.roleId]);

  const loadMesas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await mesasApi.getAll();
      if (response.success && response.data) {
        // esconder mesa default ("Sem Mesa" numero 0) da lista — só aparece como opção "Sem mesa" nas reservas
        setMesas(response.data.filter(m => !m.mesaDefault));
      }
    } catch {
      error('Erro ao carregar mesas');
    } finally {
      setLoading(false);
    }
  }, [error]);

  const loadLocais = useCallback(async () => {
    try {
      const response = await locaisApi.getAll();
      if (response.success && response.data) {
        setLocais(response.data);
      }
    } catch (err) {
      console.error('Failed to load locais:', err);
    }
  }, []);

  const loadEstadosHoje = useCallback(async () => {
    try {
      const res = await reservasApi.getMapaMesas(new Date());
      if (res.success && res.data) {
        const m = new Map<number, number>();
        res.data.mesas.forEach(mm => m.set(mm.id, mm.estado));
        setEstadosHoje(m);
      }
    } catch (err) {
      console.error('Failed to load estados hoje:', err);
    }
  }, []);

  useEffect(() => {
    loadMesas();
    loadLocais();
    loadPermissions();
    loadEstadosHoje();
  }, [loadMesas, loadLocais, loadPermissions, loadEstadosHoje]);

  const filtered = useMemo(() => {
    let list = mesas;
    if (localFiltro) list = list.filter(m => m.localId === localFiltro);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(m =>
      m.descricao.toLowerCase().includes(q) ||
      String(m.numero).includes(q) ||
      m.localDescricao.toLowerCase().includes(q)
    );
    return list;
  }, [mesas, search, localFiltro]);

  // auto-preenche descrição como "Local - #N" apenas ao criar (não ao editar)
  useEffect(() => {
    if (!editando && form.localId && form.numero) {
      const loc = locais.find(l => String(l.id) === form.localId);
      if (loc) {
        const auto = `${loc.descricao} - #${form.numero}`;
        if (form.descricao !== auto) setForm(p => ({ ...p, descricao: auto }));
      }
    }
  }, [editando, form.localId, form.numero, locais]);

  const openNovo = () => {
    const cap = String(locais[0]?.capacidadePadrao ?? 4);
    setForm({ ...emptyForm, localId: locais[0]?.id.toString() || '', capacidadePessoas: cap });
    setEditando(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEditar = (mesa: Mesa) => {
    setForm({
      localId: mesa.localId.toString(),
      descricao: mesa.descricao,
      numero: mesa.numero.toString(),
      capacidadePessoas: mesa.capacidadePessoas.toString(),
      podeReservar: mesa.podeReservar,
      mesaDefault: mesa.mesaDefault,
    });
    setEditando(mesa);
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.localId) {
      setFormError('Selecione um local');
      return;
    }
    if (!form.descricao.trim()) {
      setFormError('A descrição é obrigatória');
      return;
    }
    const numero = Number(form.numero);
    const capacidade = Number(form.capacidadePessoas);
    if (!Number.isFinite(numero) || numero < 0) {
      setFormError('Número inválido');
      return;
    }
    if (!capacidade || capacidade < 1) {
      setFormError('Capacidade inválida');
      return;
    }

    setSaving(true);
    try {
      if (editando) {
        const request: UpdateMesaRequest = {
          localId: Number(form.localId),
          descricao: form.descricao.trim(),
          numero,
          capacidadePessoas: capacidade,
          podeReservar: form.podeReservar,
          mesaDefault: form.mesaDefault,
          versao: editando.versao || 1,
        };
        const response = await mesasApi.update(editando.id, request);
        if (response.success) {
          success('Mesa atualizada com sucesso');
          setModalOpen(false);
          loadMesas();
          loadEstadosHoje();
        } else {
          setFormError(response.message || 'Erro ao atualizar mesa');
        }
      } else {
        const request: CreateMesaRequest = {
          localId: Number(form.localId),
          descricao: form.descricao.trim(),
          numero,
          capacidadePessoas: capacidade,
          podeReservar: form.podeReservar,
          mesaDefault: form.mesaDefault,
        };
        const response = await mesasApi.create(request);
        if (response.success) {
          success('Mesa criada com sucesso');
          setModalOpen(false);
          loadMesas();
          loadEstadosHoje();
        } else {
          setFormError(response.message || 'Erro ao criar mesa');
        }
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Erro ao guardar mesa');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (mesa: Mesa) => {
    if (!confirm(`Eliminar a "Mesa ${mesa.numero}"?`)) return;
    try {
      const response = await mesasApi.delete(mesa.id);
      if (response.success) {
        success('Mesa eliminada');
        loadMesas();
        loadEstadosHoje();
      } else {
        error(response.message || 'Erro ao eliminar mesa');
      }
    } catch (err) {
      console.error('Failed to delete mesa:', err);
      error('Erro ao eliminar mesa');
    }
  };

  const handleQuickCap = async (mesa: Mesa, delta: number) => {
    const nova = mesa.capacidadePessoas + delta;
    if (nova < 1 || nova > 20) return;
    try {
      const res = await mesasApi.update(mesa.id, {
        localId: mesa.localId,
        descricao: mesa.descricao,
        numero: mesa.numero,
        capacidadePessoas: nova,
        podeReservar: mesa.podeReservar,
        mesaDefault: mesa.mesaDefault,
        versao: mesa.versao,
      });
      if (res.success) {
        success(`Capacidade: ${nova} pax`);
        loadMesas();
      } else error(res.message || 'Erro');
    } catch (err) {
      console.error('Failed to update capacidade:', err);
      error('Erro ao atualizar capacidade');
    }
  };

  const handleDuplicar = async (mesa: Mesa) => {
    const proxNumero = Math.max(...mesas.filter(m => m.localId === mesa.localId).map(m => m.numero), 0) + 1;
    try {
      const res = await mesasApi.create({
        localId: mesa.localId,
        descricao: '',
        numero: proxNumero,
        capacidadePessoas: mesa.capacidadePessoas,
        podeReservar: mesa.podeReservar,
        mesaDefault: false,
      });
      if (res.success) {
        success(`Mesa ${proxNumero} duplicada`);
        loadMesas();
        loadEstadosHoje();
      } else error(res.message || 'Erro');
    } catch (err) {
      console.error('Failed to duplicate mesa:', err);
      error('Erro ao duplicar');
    }
  };

  const totalCapacidade = mesas.reduce((total, mesa) => total + mesa.capacidadePessoas, 0);
  const locaisComMesas = new Set(mesas.map(mesa => mesa.localId)).size;
  const mesasReservaveis = mesas.filter(mesa => mesa.podeReservar).length;
  const hasFilters = Boolean(search.trim() || localFiltro);
  const estadoLabel = (e: number | undefined) => e === 0 ? 'Livre' : e === 1 ? 'Reservada' : e === 2 ? 'Ocupada' : e === 3 ? 'Chega agora' : '—';
  const estadoClass = (e: number | undefined) => e === 0 ? 'badge-success' : e === 1 ? 'badge-warning' : e === 2 ? 'badge-danger' : e === 3 ? 'badge-warning' : 'badge-neutral';

  return (
    <div className="page-container mesas-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mesas</h1>
          <p className="page-subtitle">Organize a capacidade e a disponibilidade por local.</p>
        </div>
        <div className="header-actions">
          {mesaPermission.create && (
            <button className="btn btn-primary" onClick={openNovo}>
              <Plus className="w-4 h-4" />
              Nova Mesa
            </button>
          )}
        </div>
      </div>

      {!permissionLoading && !mesaPermission.access ? (
        <div className="empty-state mesas-access-denied" role="alert">
          <LayoutGrid className="w-12 h-12" />
          <h3>Acesso às mesas bloqueado</h3>
          <p>O role atribuído ao utilizador não tem permissão para consultar este recurso.</p>
        </div>
      ) : (
        <>
          <div className="mesa-summary" aria-label="Resumo das mesas">
            <div className="mesa-summary-item"><LayoutGrid className="w-5 h-5" /><span><strong>{mesas.length}</strong><small>mesas configuradas</small></span></div>
            <div className="mesa-summary-item"><UserRound className="w-5 h-5" /><span><strong>{totalCapacidade}</strong><small>lugares totais</small></span></div>
            <div className="mesa-summary-item"><UserRound className="w-5 h-5" /><span><strong>{mesasReservaveis}<small> / {mesas.length}</small></strong><small>mesas reserváveis</small></span></div>
            <div className="mesa-summary-item"><Ruler className="w-5 h-5" /><span><strong>{locaisComMesas}</strong><small>locais com mesas</small></span></div>
          </div>

          <div className="card">
            <div className="mesas-toolbar">
              <div>
                <h2 className="mesas-toolbar-title">Lista de mesas</h2>
                <p className="mesas-toolbar-meta">{hasFilters ? `${filtered.length} de ${mesas.length} mesas` : `${mesas.length} mesas no total`}</p>
              </div>
              <div className="mesas-filters">
                <div className="btn-group" role="group" aria-label="Vista">
                  <button className={`btn btn-sm ${viewMode === 'table' ? 'btn-secondary' : 'btn-ghost'}`} onClick={() => setViewMode('table')} title="Vista tabela"><Table className="w-4 h-4" /></button>
                  <button className={`btn btn-sm ${viewMode === 'grid' ? 'btn-secondary' : 'btn-ghost'}`} onClick={() => setViewMode('grid')} title="Vista grelha"><LayoutGrid className="w-4 h-4" /></button>
                </div>
                <label className="sr-only" htmlFor="mesa-local-filter">Filtrar por local</label>
                <select id="mesa-local-filter" className="input input-sm" value={localFiltro ?? ''} onChange={e => setLocalFiltro(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">Todos os locais</option>
                  {locais.map(l => (
                    <option key={l.id} value={l.id}>{l.descricao}</option>
                  ))}
                </select>
                <div className="mesas-search">
                  <Search className="w-4 h-4" aria-hidden="true" />
                  <label className="sr-only" htmlFor="mesa-search">Pesquisar mesas</label>
                  <input
                    id="mesa-search"
                    type="search"
                    placeholder="Número, descrição ou local"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="input"
                  />
                </div>
                {hasFilters && (
                  <button className="btn btn-ghost btn-icon" onClick={() => { setSearch(''); setLocalFiltro(null); }} aria-label="Limpar filtros" title="Limpar filtros">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="page-loading">
                <div className="spinner"></div>
                <p>A carregar mesas...</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="mesas-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, padding: 12 }}>
                {filtered.map(mesa => {
                  const estado = estadosHoje.get(mesa.id);
                  return (
                    <div key={mesa.id} className="card" style={{ padding: 12, borderLeft: `4px solid ${mesa.localCor}`, opacity: mesa.podeReservar ? 1 : 0.6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <strong>Mesa {mesa.numero}</strong>
                        <span className={`badge ${estadoClass(estado)}`} style={{ fontSize: 11 }}>{estadoLabel(estado)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 4 }}>{mesa.descricao}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                        <span className="badge badge-neutral" style={{ borderLeft: `3px solid ${mesa.localCor}` }}>{mesa.localDescricao}</span>
                        <span className={`badge ${mesa.podeReservar ? 'badge-success' : 'badge-warning'}`}>{mesa.podeReservar ? 'Reservável' : 'Desativada'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleQuickCap(mesa, -1)} disabled={!mesaPermission.edit} aria-label="Menos 1"><Minus className="w-3 h-3" /></button>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}><Users className="w-4 h-4" />{mesa.capacidadePessoas} pax</span>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleQuickCap(mesa, 1)} disabled={!mesaPermission.edit} aria-label="Mais 1"><Plus className="w-3 h-3" /></button>
                        <span style={{ flex: 1 }} />
                        {mesaPermission.edit && <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditar(mesa)} title="Editar"><Edit className="w-4 h-4" /></button>}
                        {mesaPermission.create && <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDuplicar(mesa)} title="Duplicar"><Copy className="w-4 h-4" /></button>}
                        {mesaPermission.delete && <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={() => handleEliminar(mesa)} title="Eliminar"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Descrição</th>
                      <th>Local</th>
                      <th>Capacidade</th>
                      <th>Hoje</th>
                      <th>Reservável</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(mesa => (
                      <tr key={mesa.id} style={!mesa.podeReservar ? { opacity: 0.55 } : undefined}>
                        <td><strong className="mesa-number">{mesa.numero}</strong></td>
                        <td><span className="mesa-description">{mesa.descricao}</span></td>
                        <td><span className="badge badge-neutral" style={{ borderLeft: `3px solid ${mesa.localCor}` }}>{mesa.localDescricao}</span></td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleQuickCap(mesa, -1)} disabled={!mesaPermission.edit} aria-label="Menos 1" style={{ padding: 2 }}><Minus className="w-3 h-3" /></button>
                            <span className="mesa-capacity"><Users className="w-4 h-4" />{mesa.capacidadePessoas}</span>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleQuickCap(mesa, 1)} disabled={!mesaPermission.edit} aria-label="Mais 1" style={{ padding: 2 }}><Plus className="w-3 h-3" /></button>
                          </span>
                        </td>
                        <td><span className={`badge ${estadoClass(estadosHoje.get(mesa.id))}`} style={{ fontSize: 11 }}>{estadoLabel(estadosHoje.get(mesa.id))}</span></td>
                        <td>
                          <span className={`badge ${mesa.podeReservar ? 'badge-success' : 'badge-warning'}`}>
                            {mesa.podeReservar ? 'Sim' : 'Desativada'}
                          </span>
                        </td>
                        <td>
                          {mesaPermission.edit || mesaPermission.delete || mesaPermission.create ? (
                            <div className="table-actions">
                              {mesaPermission.edit && <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditar(mesa)} aria-label={`Editar mesa ${mesa.numero}`} title="Editar mesa">
                                <Edit className="w-4 h-4" />
                              </button>}
                              {mesaPermission.create && <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDuplicar(mesa)} aria-label={`Duplicar mesa ${mesa.numero}`} title="Duplicar mesa">
                                <Copy className="w-4 h-4" />
                              </button>}
                              {mesaPermission.delete && <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={() => handleEliminar(mesa)} aria-label={`Eliminar mesa ${mesa.numero}`} title="Eliminar mesa">
                                <Trash2 className="w-4 h-4" />
                              </button>}
                            </div>
                          ) : (
                            <span className="text-sm">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="empty-state">
                <Users className="w-12 h-12 text-gray-300" />
                <h3>{search ? 'Nenhum resultado' : 'Nenhuma mesa configurada'}</h3>
                <p>{search ? `Nenhuma mesa para "${search}"` : 'Adicione a primeira mesa ou configure o intervalo no Local'}</p>
                {mesaPermission.create && !search && (
                  <button className="btn btn-primary mt-4" onClick={openNovo}>
                    <Plus className="w-4 h-4" />
                    Nova Mesa
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <Modal
        title={editando ? 'Editar Mesa' : 'Nova Mesa'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" form="mesa-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'A guardar...' : editando ? 'Atualizar' : 'Criar'}
            </button>
          </>
        }
      >
        <form id="mesa-form" onSubmit={handleSubmit} noValidate>
          {formError && (
            <div className="form-error" role="alert">
              {formError}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="localId" className="label">Local <span className="required">*</span></label>
              <select
                id="localId"
                className="input"
                value={form.localId}
                onChange={e => setForm(p => ({ ...p, localId: e.target.value }))}
                required
              >
                <option value="">Selecionar local...</option>
                {locais.map(l => (
                  <option key={l.id} value={l.id}>{l.descricao}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="numero" className="label">Número <span className="required">*</span></label>
              <input
                type="number"
                id="numero"
                className="input"
                value={form.numero}
                onChange={e => setForm(p => ({ ...p, numero: e.target.value }))}
                placeholder="1"
                min={0}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="descricao" className="label">Nome (auto: Local - #N)</label>
            <input
              type="text"
              id="descricao"
              className="input"
              value={form.descricao}
              onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))}
              placeholder="Ex: Salão - #1"
            />
            <span className="form-hint">Gerado automaticamente a partir do local e número (editável)</span>
          </div>

          <div className="form-group">
            <label htmlFor="capacidadePessoas" className="label">Capacidade (pessoas) <span className="required">*</span></label>
            <input
              type="number"
              id="capacidadePessoas"
              className="input"
              value={form.capacidadePessoas}
              onChange={e => setForm(p => ({ ...p, capacidadePessoas: e.target.value }))}
              min={1}
              required
            />
          </div>

          <div className="form-group form-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.podeReservar}
                onChange={e => setForm(p => ({ ...p, podeReservar: e.target.checked }))}
              />
              <span className="checkbox-custom" aria-hidden="true"></span>
              <span>Pode reservar (se desmarcado fica desabilitada)</span>
            </label>
          </div>
          <div className="form-group form-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.mesaDefault}
                onChange={e => setForm(p => ({ ...p, mesaDefault: e.target.checked }))}
              />
              <span className="checkbox-custom" aria-hidden="true"></span>
              <span>Mesa default (Sem Mesa - usado para reservas sem mesa atribuída)</span>
            </label>
          </div>
        </form>
      </Modal>
    </div>
  );
}
